import { useEffect, useState, useCallback } from "react";
import {
    Server,
    Database,
    HardDrive,
    Activity,
    RefreshCw,
    CheckCircle2,
    XCircle,
    AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";

function formatBytes(n) {
    if (n == null || isNaN(n)) return "-";
    n = Number(n);
    if (n < 1024) return `${Math.round(n)} B`;
    const units = ["KB", "MB", "GB", "TB"];
    for (let i = 0; i < units.length; i++) {
        n /= 1024;
        if (n < 1024) return `${n.toFixed(2)} ${units[i]}`;
    }
    return `${n.toFixed(2)} PB`;
}

function formatUptime(seconds) {
    if (seconds == null || isNaN(seconds)) return "-";
    seconds = Math.max(0, Math.floor(seconds));
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

const SERVICE_META = {
    backend: { label: "Backend (FastAPI)", icon: Server },
    frontend: { label: "Frontend (React)", icon: Server },
    mongodb: { label: "MongoDB", icon: Database },
};

function StatusBadge({ state }) {
    const s = String(state || "").toUpperCase();
    if (s === "RUNNING") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-xs font-semibold uppercase tracking-wider">
                <CheckCircle2 size={12} /> Running
            </span>
        );
    }
    if (s === "STOPPED" || s === "FATAL" || s === "EXITED") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 text-xs font-semibold uppercase tracking-wider">
                <XCircle size={12} /> {s.toLowerCase()}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/30 text-xs font-semibold uppercase tracking-wider">
            <AlertCircle size={12} /> {s.toLowerCase() || "unknown"}
        </span>
    );
}

function Stat({ label, value, sub }) {
    return (
        <div className="rounded-lg border border-white/10 bg-[color:var(--bg-base)]/40 p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-muted)]">
                {label}
            </div>
            <div className="text-xl text-white font-semibold mt-1">{value}</div>
            {sub && (
                <div className="text-xs text-[color:var(--text-muted)] mt-1">{sub}</div>
            )}
        </div>
    );
}

function ProgressBar({ used, total }) {
    if (!total) return null;
    const pct = Math.min(100, Math.max(0, (used / total) * 100));
    const color =
        pct > 90 ? "bg-red-500" : pct > 70 ? "bg-yellow-500" : "bg-emerald-500";
    return (
        <div className="mt-2">
            <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                <div
                    className={`h-full ${color} transition-all`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="flex justify-between mt-1 text-[11px] text-[color:var(--text-muted)]">
                <span>{formatBytes(used)} / {formatBytes(total)}</span>
                <span>{pct.toFixed(1)}%</span>
            </div>
        </div>
    );
}

export default function SystemMonitor() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [refreshing, setRefreshing] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);

    const load = useCallback(async () => {
        setRefreshing(true);
        try {
            const res = await api.get("/admin/system-status");
            setData(res.data);
            setError("");
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Nie udało się pobrać statusu systemu.");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!autoRefresh) return undefined;
        const id = setInterval(load, 10000);
        return () => clearInterval(id);
    }, [autoRefresh, load]);

    if (loading) {
        return (
            <div className="text-[color:var(--text-muted)] py-8 text-sm">
                Wczytywanie statusu systemu…
            </div>
        );
    }
    if (error) {
        return (
            <div
                data-testid="monitor-error"
                className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-3 rounded-md text-sm"
            >
                {error}
            </div>
        );
    }
    if (!data) return null;

    const services = data.services || {};
    const dbInfo = data.database || {};
    const host = data.host || {};
    const checkedAt = data.checked_at ? new Date(data.checked_at).toLocaleTimeString("pl-PL") : "";

    return (
        <div data-testid="system-monitor" className="space-y-8">
            {/* Header + controls */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-[color:var(--text-muted)] text-xs">
                    Ostatnie sprawdzenie:{" "}
                    <span className="text-white font-medium">{checkedAt || "-"}</span>
                    {data.database?.mongo_version && (
                        <span className="ml-3">
                            MongoDB v
                            <span className="text-white font-medium">
                                {data.database.mongo_version}
                            </span>
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)] cursor-pointer">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                            className="accent-[color:var(--primary)]"
                        />
                        Auto-odświeżanie (10s)
                    </label>
                    <button
                        data-testid="monitor-refresh-btn"
                        onClick={load}
                        disabled={refreshing}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-xs text-white border border-white/10 disabled:opacity-50"
                    >
                        <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
                        Odśwież
                    </button>
                </div>
            </div>

            {/* Services */}
            <section>
                <div className="flex items-center gap-2 mb-3">
                    <Activity size={16} className="text-[color:var(--primary)]" />
                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-white">
                        Status usług
                    </h3>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {["backend", "frontend", "mongodb"].map((key) => {
                        const meta = SERVICE_META[key];
                        const Icon = meta.icon;
                        const svc = services[key] || {};
                        return (
                            <div
                                key={key}
                                data-testid={`monitor-service-${key}`}
                                className="rounded-lg border border-white/10 bg-[color:var(--bg-base)]/40 p-4"
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2 text-white font-semibold text-sm">
                                        <Icon size={15} className="text-[color:var(--primary)]" />
                                        {meta.label}
                                    </div>
                                    <StatusBadge state={svc.state} />
                                </div>
                                <div className="text-xs text-[color:var(--text-muted)] space-y-1">
                                    <div>
                                        Uptime:{" "}
                                        <span className="text-white">
                                            {formatUptime(svc.uptime_seconds)}
                                        </span>
                                    </div>
                                    {svc.pid != null && (
                                        <div>
                                            PID:{" "}
                                            <span className="text-white">{svc.pid}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Database size */}
            <section>
                <div className="flex items-center gap-2 mb-3">
                    <Database size={16} className="text-[color:var(--primary)]" />
                    <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-white">
                        Rozmiar bazy danych
                    </h3>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <Stat
                        label="Rozmiar danych"
                        value={formatBytes(dbInfo.data_size_bytes)}
                        sub={`${dbInfo.objects ?? 0} dokumentów`}
                    />
                    <Stat
                        label="Rozmiar na dysku"
                        value={formatBytes(dbInfo.storage_size_bytes)}
                        sub="z kompresją WiredTiger"
                    />
                    <Stat
                        label="Rozmiar indeksów"
                        value={formatBytes(dbInfo.index_size_bytes)}
                        sub={`${dbInfo.indexes ?? 0} indeksów`}
                    />
                    <Stat
                        label="Łącznie (DB + indeksy)"
                        value={formatBytes(dbInfo.total_size_bytes)}
                        sub={`${dbInfo.collections ?? 0} kolekcji`}
                    />
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-white/10 bg-[color:var(--bg-base)]/40 p-4">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-muted)]">
                            Pliki w GridFS (media)
                        </div>
                        <div className="text-xl text-white font-semibold mt-1">
                            {formatBytes(dbInfo.media_size_bytes)}
                        </div>
                        <div className="text-xs text-[color:var(--text-muted)] mt-1">
                            {dbInfo.media_count ?? 0} plików (zdjęcia, mapa, mod ZIP, logo)
                        </div>
                    </div>
                    <div className="rounded-lg border border-white/10 bg-[color:var(--bg-base)]/40 p-4">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-muted)] flex items-center gap-2">
                            <HardDrive size={12} /> Wolne miejsce na dysku
                        </div>
                        <ProgressBar
                            used={host.disk_used_bytes}
                            total={host.disk_total_bytes}
                        />
                        <div className="text-xs text-[color:var(--text-muted)] mt-2">
                            Wolne:{" "}
                            <span className="text-white font-medium">
                                {formatBytes(host.disk_free_bytes)}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Per-collection breakdown */}
            {Array.isArray(dbInfo.collections_list) && dbInfo.collections_list.length > 0 && (
                <section>
                    <div className="flex items-center gap-2 mb-3">
                        <Database size={16} className="text-[color:var(--primary)]" />
                        <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-white">
                            Kolekcje
                        </h3>
                    </div>
                    <div className="rounded-lg border border-white/10 overflow-hidden">
                        <table className="w-full text-sm" data-testid="monitor-collections-table">
                            <thead className="bg-white/[0.03] text-[color:var(--text-muted)] text-[10px] uppercase tracking-[0.18em]">
                                <tr>
                                    <th className="text-left px-4 py-2">Kolekcja</th>
                                    <th className="text-right px-4 py-2">Dokumenty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dbInfo.collections_list.map((c) => (
                                    <tr
                                        key={c.name}
                                        className="border-t border-white/5 hover:bg-white/[0.02]"
                                    >
                                        <td className="px-4 py-2 text-white">{c.name}</td>
                                        <td className="px-4 py-2 text-right text-[color:var(--text-secondary)]">
                                            {c.documents == null ? "-" : c.documents.toLocaleString("pl-PL")}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
}
